import type { EstimateResponse, EstimateUsageBasis, MoneyWire } from '@rheora/enrollment-contract';

/**
 * Presentation helpers for the E-SF04 "your estimated bill" screen.
 *
 * ── NO FLOAT MONEY MATH ─────────────────────────────────────────────────────
 * Every number arrives from the BFF as an exact minor-unit / scaled-integer
 * string. Nothing here does floating-point arithmetic: money is formatted from
 * BigInt cents, and the effective-rate string (¢/kWh × 10⁴) is scaled down with
 * BigInt. No client-side re-pricing — the screen only formats what the server
 * computed.
 *
 * ── AC2 — NEVER A GUARANTEED PRICE ─────────────────────────────────────────
 * The response is typed `isEstimate: true`; the screen always renders the
 * disclaimer and this module never produces a "guaranteed" or "final price"
 * string.
 */

/** Exact minor-unit money → "$X.YY", scale-aware, BigInt only. */
export function formatMoneyWireToDollars(money: MoneyWire): string {
  const trimmed = money.amount_minor.trim();
  const negative = trimmed.startsWith('-');
  const digits = (negative ? trimmed.slice(1) : trimmed).replace(/\D/gu, '');
  if (digits.length === 0) return '$0.00';
  const minor = BigInt(digits);
  const divisor = 10n ** BigInt(money.scale);
  const whole = minor / divisor;
  const frac = (minor % divisor).toString().padStart(money.scale, '0');
  // USD is scale 2; render at most 2 fractional digits for the customer.
  const shown = money.scale <= 2 ? frac : frac.slice(0, 2);
  return `${negative ? '-' : ''}$${whole.toString()}.${shown.padEnd(2, '0')}`;
}

/**
 * Effective rate (¢/kWh × 10⁴, integer string) → a "12.3" ¢/kWh string, rounded
 * to one decimal place (matching the shelf's §25.475 headline precision). BigInt
 * only — no float.
 */
export function formatEffectiveRateMinor(minor: string): string {
  const digits = minor.trim().replace(/\D/gu, '');
  if (digits.length === 0) return '0.0';
  // value = digits / 10⁴ ¢/kWh. Round to tenths: round(digits / 1000).
  const tenths = (BigInt(digits) + 500n) / 1000n;
  const whole = tenths / 10n;
  const frac = tenths % 10n;
  return `${whole.toString()}.${frac.toString()}`;
}

/** True when the estimate used the home-size PROFILE fallback (AC1). */
export function isProfileFallback(usage: EstimateUsageBasis): boolean {
  return usage.source === 'profile';
}

/**
 * The i18n key describing where the estimate's usage basis came from — and the
 * one place the screen is allowed to explain itself (E-SF19 P0a).
 *
 * ── THE DEFECT THIS CLOSES ─────────────────────────────────────────────────
 * There were only two branches, `history` and `profile`, so EVERY estimate that
 * was not read from GMO history rendered the profile sentence: "Based on a
 * typical home (about 1,000 kWh/mo) — we couldn't find usage history for this
 * address". For a shopper who had just typed 120 kWh/mo into the consumption
 * step that sentence was FALSE in both halves. We had not failed to find their
 * usage; we had discarded the figure they gave us. And the reason offered for the
 * substitution was invented, not observed.
 *
 * Each `source` the server can report now has its OWN sentence, and the server
 * reports the basis it actually priced at. There is no default branch that can
 * quietly absorb a new source and describe it as something else: an unrecognised
 * source falls to the neutral key, which claims nothing about why.
 *
 * ⚠️ NEVER ADD A BRANCH THAT EXPLAINS A SOURCE THE SERVER DID NOT REPORT.
 */
export function usageBasisLabelKey(usage: EstimateUsageBasis): string {
  switch (usage.source) {
    case 'declared':
      return 'shop.estimate.basis.declared';
    case 'history':
      return 'shop.estimate.basis.history';
    case 'profile':
      return profileBasisLabelKey(usage.profileSize);
    default:
      return 'shop.estimate.basis.unknown';
  }
}

/** The profile sentence for a size — the ONLY path that may mention missing history. */
function profileBasisLabelKey(size: EstimateUsageBasis['profileSize']): string {
  switch (size) {
    case 'small':
      return 'shop.estimate.basis.profile.small';
    case 'large':
      return 'shop.estimate.basis.profile.large';
    case 'medium':
    default:
      return 'shop.estimate.basis.profile.medium';
  }
}

/**
 * The i18n key disclosing that the bill was projected OUTSIDE the three published
 * §25.475 reference usages, or `null` when it was not.
 *
 * PUCT publishes an EFL at 500 / 1000 / 2000 kWh only. A customer at 120 or 3,500
 * kWh/mo is being shown a figure derived by extending that published curve, not
 * read off it — a materially different claim, and one the screen has to make out
 * loud rather than let the customer assume they are reading a published price.
 *
 * Returns a key rather than a boolean so the component stays render-only and the
 * copy stays in `locales/`.
 */
export function outsideReferenceRangeNoticeKey(usage: EstimateUsageBasis): string | null {
  if (!usage.outsideReferenceRange) return null;
  return usage.monthlyKwh < LOWEST_REFERENCE_USAGE_KWH
    ? 'shop.estimate.basis.belowReferenceRange'
    : 'shop.estimate.basis.aboveReferenceRange';
}

/**
 * The lowest PUCT §25.475 reference usage. Used ONLY to decide which side of the
 * published band a usage fell on for the disclosure above — never to price
 * anything (the server owns every number the screen renders).
 */
const LOWEST_REFERENCE_USAGE_KWH = 500;

/** Which of the three reference tiers the premise's usage falls closest to. */
export function activeCurveIndex(estimate: EstimateResponse): number {
  const usage = estimate.usage.monthlyKwh;
  let bestIndex = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  estimate.curve.forEach((point, index) => {
    const distance = Math.abs(point.usageKwh - usage);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });
  return bestIndex;
}

/** How a SWITCH customer is answering "when should your service start?". */
export type StartDateMode = 'FIRST_AVAILABLE' | 'CHOSEN';

/**
 * The start-date choice the picker shows, given what the customer has touched.
 *
 * `startMode` / `chosenDate` are `null` until the customer changes them. Until
 * then the default is:
 *  - an OFFER session (`constraints.offeredDate` set): the date the agent put on
 *    the quote in the CRM, preselected as a chosen date. QA (CRM path): "Selecting
 *    a future start date still sets the next date as the start date" — this step
 *    used to default to "First available" and send it, which moved the agent's
 *    date to the next business day for a customer who changed nothing.
 *  - otherwise: "First available", with no chosen date.
 *
 * Anything the customer HAS touched wins, including a deliberate "First available".
 */
export function resolveStartDateChoice(
  offeredDate: string | undefined,
  touched: { readonly startMode: StartDateMode | null; readonly chosenDate: string | null },
): { readonly startMode: StartDateMode; readonly chosenDate: string } {
  return {
    startMode: touched.startMode ?? (offeredDate !== undefined ? 'CHOSEN' : 'FIRST_AVAILABLE'),
    chosenDate: touched.chosenDate ?? offeredDate ?? '',
  };
}
